import { FC } from 'react'
import { alpha, Box, Badge, Typography, ButtonBase } from '@mui/material'
import { CheckCircle, ErrorOutline, ScheduleOutlined } from '@mui/icons-material'

import { ContractDocumentTypes } from 'services/contracts/contracts.consts'
import { useIntl } from 'react-intl'
import theme from 'styles/theme'

import { formatMoney } from './leases.utils'

interface PaymentScheduleDocumnetProps {
  documentId: number
  amount: string
  data: any
  type: ContractDocumentTypes
  number?: string
  discount?: number
  file?: string
  currency?: string
  isPaid: boolean
  paidAmount: string
  monthLabel: string
  dueDateLabel: string
  onOpen?: () => void
}

const TO_PAY_COLOR = '#D4A800'

// Payment document card — mobile list row ("Panel Klienta 3.0").
const PaymentScheduleDocumnet: FC<PaymentScheduleDocumnetProps> = ({
  amount,
  number,
  discount,
  currency = '',
  isPaid,
  monthLabel,
  dueDateLabel,
  onOpen
}) => {
  const { formatMessage } = useIntl()

  const status = !number
    ? {
        label: formatMessage({ id: 'paymentSchedule.statusAwaiting', defaultMessage: 'Oczekująca' }),
        color: theme.r4r.grey,
        backgroundColor: theme.r4r.greyLight,
        Icon: ScheduleOutlined
      }
    : isPaid
    ? {
        label: formatMessage({ id: 'paymentSchedule.statusPaid', defaultMessage: 'Opłacona' }),
        color: theme.r4r.green,
        backgroundColor: alpha(theme.r4r.green, 0.1),
        Icon: CheckCircle
      }
    : {
        label: formatMessage({ id: 'paymentSchedule.statusToPay', defaultMessage: 'Do zapłaty' }),
        color: TO_PAY_COLOR,
        backgroundColor: alpha(theme.r4r.yellow, 0.15),
        Icon: ErrorOutline
      }

  return (
    <Box sx={{ backgroundColor: '#fff', boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)' }}>
      <ButtonBase
        onClick={onOpen}
        disabled={!onOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          textAlign: 'left',
          gap: '12px',
          padding: '14px 16px',
          '&.Mui-disabled': { opacity: 1 }
        }}>
        <status.Icon sx={{ fontSize: '18px', color: status.color, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: theme.r4r.ink,
              textTransform: 'capitalize'
            }}>
            {monthLabel}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
            {number
              ? dueDateLabel
              : formatMessage({
                  id: 'paymentScheduleDocumnet.awaiting',
                  defaultMessage: 'Oczekuje na wystawienie'
                })}
          </Typography>
        </Box>
        {!!discount && <Badge color='primary' badgeContent={discount} sx={{ mr: 1 }} />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '4px',
            flexShrink: 0
          }}>
          {!!amount && (
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.ink }}>
              {formatMoney(amount, currency)}
            </Typography>
          )}
          <Box sx={{ backgroundColor: status.backgroundColor, padding: '3px 10px' }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: status.color,
                whiteSpace: 'nowrap'
              }}>
              {status.label}
            </Typography>
          </Box>
        </Box>
      </ButtonBase>
    </Box>
  )
}

export default PaymentScheduleDocumnet
